import {
	REPOSITORY_COPY,
	REPOSITORY_SYSTEM_OPTIONS,
} from '#app/routes/mocra+/_form+/components/repositories/constants.ts'
import { type RepositoryEntry } from '#app/routes/mocra+/_form+/components/repositories/schema.ts'

export type RepositorySystemValue =
	(typeof REPOSITORY_SYSTEM_OPTIONS)[number]['value']

export type RepositoryDraft = {
	system: RepositorySystemValue
	location: string
}

const DEFAULT_REPOSITORY_SYSTEM: RepositorySystemValue =
	REPOSITORY_SYSTEM_OPTIONS[0]?.value ?? 'sharepoint'

export function createRepositoryDraft(
	overrides: Partial<RepositoryDraft> = {},
): RepositoryDraft {
	return {
		system: DEFAULT_REPOSITORY_SYSTEM,
		location: '',
		...overrides,
	}
}

function normalizeRepositorySystem(value: unknown): RepositorySystemValue {
	if (typeof value !== 'string') return DEFAULT_REPOSITORY_SYSTEM
	const trimmed = value.trim()
	if (!trimmed) return DEFAULT_REPOSITORY_SYSTEM

	const direct = REPOSITORY_SYSTEM_OPTIONS.find(
		(option) => option.value === trimmed,
	)
	if (direct) {
		return direct.value
	}

	const lower = trimmed.toLowerCase()
	if (lower.includes('sharepoint') || lower.includes('onedrive')) {
		return 'sharepoint'
	}
	if (
		lower.includes('airtable') ||
		lower.includes('notion') ||
		lower.includes('database') ||
		lower.includes('sheet')
	) {
		return 'airtable'
	}
	if (lower.includes('plm') || lower.includes('formulation')) {
		return 'plm'
	}
	if (
		lower.includes('qms') ||
		lower.includes('eqms') ||
		lower.includes('qualio') ||
		lower.includes('etq')
	) {
		return 'qms'
	}
	if (
		lower.includes('erp') ||
		lower.includes('oracle') ||
		lower.includes('sap') ||
		lower.includes('netsuite') ||
		lower.includes('dynamics')
	) {
		return 'erp'
	}
	return DEFAULT_REPOSITORY_SYSTEM
}

export function sanitizeRepositoryDraft(
	draft: RepositoryDraft,
): RepositoryDraft {
	const system = normalizeRepositorySystem(draft.system)
	return {
		system,
		location: draft.location.trim(),
	}
}

export function collectRepositoryErrors(draft: RepositoryDraft): string[] {
	const sanitized = sanitizeRepositoryDraft(draft)
	const errors: string[] = []
	if (sanitized.location.length === 0) {
		errors.push('Enter the folder path or location.')
	}
	return errors
}

export function repositoryCardTitle(draft: RepositoryDraft): string {
	const sanitized = sanitizeRepositoryDraft(draft)
	return resolveRepositorySystemLabel(sanitized.system)
}

export function formatRepositorySummary(draft: RepositoryDraft): string {
	const sanitized = sanitizeRepositoryDraft(draft)
	return sanitized.location || REPOSITORY_COPY.summary.locationMissing
}

export function resolveRepositorySystemLabel(
	value: RepositorySystemValue,
): string {
	return (
		REPOSITORY_SYSTEM_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function isRepositorySystemValue(
	value: unknown,
): value is RepositorySystemValue {
	return REPOSITORY_SYSTEM_OPTIONS.some((option) => option.value === value)
}

export function repositoryEntryToDraft(
	entry: RepositoryEntry,
): RepositoryDraft {
	return sanitizeRepositoryDraft(
		createRepositoryDraft({
			system: normalizeRepositorySystem(entry.system),
			location: entry.location,
		}),
	)
}

export function repositoryDraftToEntry(
	draft: RepositoryDraft,
): RepositoryEntry {
	const sanitized = sanitizeRepositoryDraft(draft)
	return {
		system: sanitized.system as RepositoryEntry['system'],
		location: sanitized.location,
	}
}
